import React, { useState } from "react";
import { COLORS } from "../utils/constants";
import { Menu, MenuItem, Button } from "@mui/material";

/**
 * MusicNoteSelector component for selecting musical notes with optional accidentals
 * Displays notes in the format C#, B♭, etc.
 */
const MusicNoteSelector = ({ value, onChange }) => {
  // State for menu handling
  const [accidentalAnchorEl, setAccidentalAnchorEl] = useState(null);
  const [noteAnchorEl, setNoteAnchorEl] = useState(null);
  
  const accidentals = [
    { value: "", label: "Natural (♮)" },
    { value: "#", label: "Sharp (#)" },
    { value: "♭", label: "Flat (♭)" }
  ];
  
  const notes = ["C", "D", "E", "F", "G", "A", "B"];
  
  // Menu handling functions
  const handleAccidentalMenuOpen = (event) => {
    setAccidentalAnchorEl(event.currentTarget);
  };

  const handleAccidentalMenuClose = () => {
    setAccidentalAnchorEl(null);
  };
  
  const handleNoteMenuOpen = (event) => {
    setNoteAnchorEl(event.currentTarget);
  };

  const handleNoteMenuClose = () => {
    setNoteAnchorEl(null);
  };
  
  // Handle selection
  const handleAccidentalSelect = (selectedAccidental) => {
    onChange({ ...value, accidental: selectedAccidental });
    handleAccidentalMenuClose();
  };
  
  const handleNoteSelect = (selectedNote) => {
    onChange({ ...value, note: selectedNote });
    handleNoteMenuClose();
  };
  
  // Get display label for the current accidental
  const getAccidentalDisplayLabel = () => {
    const selected = accidentals.find(acc => acc.value === value.accidental);
    return selected ? selected.label : "Select Accidental";
  };
  
  return (
    <div className="flex items-center space-x-4">
      <div>
        <label className="text-sm font-medium block mb-1" style={{ color: COLORS.TEXT_SECONDARY }}>Accidental</label>
        <Button
          onClick={handleAccidentalMenuOpen}
          variant="outlined"
          className="w-full justify-between normal-case"
          style={{ 
            textTransform: 'none', 
            justifyContent: 'space-between',
            padding: '0.25rem 0.5rem',
            border: '1px solid #d1d5db',
            color: 'black',
            backgroundColor: 'white',
            minWidth: '150px'
          }}
        >
          {getAccidentalDisplayLabel()}
        </Button>
        <Menu
          anchorEl={accidentalAnchorEl}
          open={Boolean(accidentalAnchorEl)}
          onClose={handleAccidentalMenuClose}
        >
          {accidentals.map(acc => (
            <MenuItem 
              key={acc.value} 
              onClick={() => handleAccidentalSelect(acc.value)}
              selected={value.accidental === acc.value}
            >
              {acc.label}
            </MenuItem>
          ))}
        </Menu>
      </div>
      
      <div>
        <label className="text-sm font-medium block mb-1" style={{ color: COLORS.TEXT_SECONDARY }}>Note</label>
        <Button
          onClick={handleNoteMenuOpen}
          variant="outlined"
          className="w-full justify-between normal-case"
          style={{ 
            textTransform: 'none', 
            justifyContent: 'space-between',
            padding: '0.25rem 0.5rem',
            border: '1px solid #d1d5db',
            color: 'black',
            backgroundColor: 'white',
            minWidth: '100px'
          }}
        >
          {value.note || "Select Note"}
        </Button>
        <Menu
          anchorEl={noteAnchorEl}
          open={Boolean(noteAnchorEl)}
          onClose={handleNoteMenuClose}
        >
          {notes.map(note => (
            <MenuItem 
              key={note} 
              onClick={() => handleNoteSelect(note)}
              selected={value.note === note}
            >
              {note}
            </MenuItem>
          ))}
        </Menu>
      </div>
      
      <div className="ml-2">
        <span className="text-lg font-medium">
          {value.note}{value.accidental}
        </span>
      </div>
    </div>
  );
};

export default MusicNoteSelector;
